import * as React from "react";
import { ChevronDown } from "lucide-react";

import { formatDate } from "./lib/date";
import { SOURCE_TYPE_ICON, sourceDate, type SourceType } from "./lib/sources";
import { FOCUS_RING, FOCUS_RING_INSET_AFTER } from "./lib/focus";
import { cn } from "./lib/utils";
import { MachineScopeSlot } from "./MachineScopeMarker";
import { Icon } from "./ui/icon";
import { LIST_ROW_ACTIONS_REVEAL, LIST_ROW_HOVER } from "./ui/list-item";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

/**
 * v2 SourceCard (redesign phase 5, round 10, #455): one source the answer was
 * drawn from, as a row in a SourceList (break #141). Presentational: the v1
 * `MessageReferences` keeps its Apollo queries, presigned URLs, telemetry and
 * open paths and renders these rows (04, "Sources").
 *
 * A row, not a card: sources are scanned as a list, and a bordered box per
 * source (v1) stacks borders in a 360 panel. A single source that stands
 * alone sits in a compact Card as a one-row list.
 *
 * Row: type icon 12 (`lib/sources.ts`, shared with the AI answer block), the
 * title (Sora 12 Semibold, two lines; a tooltip with the full title only
 * when it is cut), the meta line ("Stand: 06.10.2026 · S. 12 · #4711", 12
 * `--ink-muted`), the snippet (Inter 12 `--ink-muted`, in full, line breaks
 * kept, behind a toggle after the title: closed by default, open while
 * highlighted), then the machine-scope slot and the actions at the end. 12px, not the
 * library's 14: sources sit under the answer or facts they back and must not read
 * as loud as them. Compact (long
 * case lists): the title on one line, meta and snippet cut together on the
 * second, so the title keeps the row's width in a 360 panel.
 *
 * Click target: the title is the link (`href`, new tab) or button (`onOpen`).
 * On a row with a hidden snippet, the snippet toggle's `::after` covers the row
 * (the ListItem pattern): a click anywhere expands it, and the title, above
 * the overlay and underlined on hover, opens the source. On any other row the
 * title's own `::after` covers it, so the whole row opens the source. The
 * marker and the kebab sit above the overlay as their own tab stops.
 *
 * Machine scope at the END (v1 put it first): one leading column keeps titles
 * aligned in a 360 panel, the reserved end slot keeps the markers on one
 * vertical line, and the fact rows carry it there too.
 *
 * `highlighted` (hover sync with a fact or the answer) uses `--primary-subtle`,
 * the linked-highlight look; it is not a selection, so no `aria-current`.
 */

export type SourceCardDensity = "default" | "compact";

export interface SourceCardLabels {
  /** The content date: `(date) => "Stand: " + date`. */
  date: (formattedDate: string) => string;
  /** `(page) => "S. " + page` */
  page: (page: number) => string;
  /** Hidden after a link's title: "öffnet in neuem Tab". */
  newTab: string;
  /** Name of the toggle that shows or hides the snippet: "Auszug". */
  excerpt: string;
}

/** Set by SourceList: compact rows for long lists (the app decides when). */
export const SourceDensityContext = React.createContext<SourceCardDensity>("default");
/** Set by SourceGroup: its passages drop the type icon and are named after the document. */
export const SourceGroupContext = React.createContext<{ title: string } | null>(null);

export interface SourceCardProps extends Omit<React.HTMLAttributes<HTMLLIElement>, "title" | "children"> {
  type: SourceType;
  title: string;
  /** Opens in a new tab. */
  href?: string;
  /**
   * Without `href`: the in-app open (dialog, Outlook, POST-mode ticket), rendered as a button.
   * With `href`: the link's click handler (e.g. v1 `handleDocumentAnchorClick`, which may
   * `preventDefault()` and open the document itself).
   */
  onOpen?: (event: React.MouseEvent<HTMLElement>) => void;
  /** "#4711", a table row's key "123, A": shown in the meta line. */
  identifier?: string | null;
  /** When the content is from: ISO `yyyy-mm-dd` or a Date. Undated or invalid → nothing. */
  date?: string | Date | null;
  /** 1-based page; 0 / null → nothing. */
  page?: number | null;
  /** The machine-scope sentence; empty → the slot stays empty. */
  machineScope?: string | null;
  snippet?: React.ReactNode;
  highlighted?: boolean;
  /** Overrides the SourceList's density. */
  density?: SourceCardDensity;
  /** Usually one kebab DropdownMenu ("Aus Speicher löschen"). */
  actions?: React.ReactNode;
  /** Under the snippet, e.g. a database row's preview (an InfoList). */
  children?: React.ReactNode;
  labels: SourceCardLabels;
  /** Static states for stories; apps never set them. */
  "data-hover"?: string;
}

const TITLE = "min-w-0 text-left font-sans text-caption font-semibold text-ink outline-hidden wrap-break-word";
const TITLE_TARGET = cn(
  "cursor-pointer after:absolute after:inset-0",
  FOCUS_RING_INSET_AFTER,
);
// A title above a row-wide overlay that does something else: only the text is the link.
const TITLE_LINK = cn("relative z-10 cursor-pointer rounded-sm hover:underline underline-offset-2", FOCUS_RING);

export const SourceCard = React.forwardRef<HTMLLIElement, SourceCardProps>(function SourceCard(
  {
    type,
    title,
    href,
    onOpen,
    identifier,
    date,
    page,
    machineScope,
    snippet,
    highlighted = false,
    density: densityProp,
    actions,
    children,
    labels,
    className,
    ...props
  },
  ref,
) {
  const listDensity = React.useContext(SourceDensityContext);
  const group = React.useContext(SourceGroupContext);
  const compact = (densityProp ?? listDensity) === "compact";
  const interactive = !!href || !!onOpen;

  const titleRef = React.useRef<HTMLElement | null>(null);
  const [chosenOpen, setChosenOpen] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const content = sourceDate(date);
  const meta = [
    content ? labels.date(formatDate(content)) : null,
    page ? labels.page(page) : null,
    identifier || null,
  ].filter((part): part is string => !!part);

  const hasSnippet = snippet != null && snippet !== "";
  // No title (a passage whose page is unknown, under its document's header): the snippet
  // itself is the link, so the row has no empty or repeated heading.
  const snippetIsTitle = !title && hasSnippet;
  // A snippet or a row's columns can run long, so they start hidden and the list shows
  // its titles at a glance. Not in a compact list (the snippet is one cut line there) or
  // in a group (opening the group is asking for its passages). A highlighted row is the
  // one the answer points at, so it shows its snippet whatever the reader chose.
  const collapsible = !group && !compact && !snippetIsTitle && (hasSnippet || children != null);
  const open = !collapsible || chosenOpen || highlighted;
  // A collapsible row expands wherever it is clicked (the chevron's overlay covers it); its
  // title is a link above that overlay. Any other row opens from anywhere (the title's).
  const titleTarget = collapsible ? interactive && TITLE_LINK : interactive && TITLE_TARGET;
  const titleText = (
    <>
      {/* A passage is named after its document: "Betriebsanleitung HX-200, S. 12". */}
      {group && (
        <>
          <span className="sr-only">{group.title},</span>{" "}
        </>
      )}
      {snippetIsTitle ? snippet : title}
    </>
  );
  const titleClass = snippetIsTitle
    ? cn(
        "min-w-0 text-left font-body text-caption text-ink-muted outline-hidden whitespace-pre-line wrap-break-word",
        titleTarget,
      )
    : cn(TITLE, compact ? "truncate" : "line-clamp-2", titleTarget);
  const setTitleRef = (el: HTMLElement | null) => {
    titleRef.current = el;
  };

  const titleElement = href ? (
    <a ref={setTitleRef} href={href} target="_blank" rel="noreferrer" onClick={onOpen} className={titleClass}>
      {titleText}{" "}
      <span className="sr-only">({labels.newTab})</span>
    </a>
  ) : onOpen ? (
    <button ref={setTitleRef} type="button" onClick={onOpen} className={titleClass}>
      {titleText}
    </button>
  ) : (
    <span ref={setTitleRef} className={titleClass}>
      {titleText}
    </span>
  );

  // The full title only when it is actually cut (measured on open); the name is the title already.
  const titleWithTooltip = snippetIsTitle ? (
    titleElement
  ) : (
    <Tooltip
      open={tooltipOpen}
      onOpenChange={(open) => {
        const el = titleRef.current;
        setTooltipOpen(open && !!el && (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight));
      }}
    >
      <TooltipTrigger asChild aria-describedby={undefined}>
        {titleElement}
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );

  const metaLine =
    meta.length > 0
      ? meta.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span aria-hidden="true"> · </span>}
            {index > 0 && <span className="sr-only">,</span>}
            {index > 0 && " "}
            <span>{part}</span>
          </React.Fragment>
        ))
      : null;

  return (
    <li
      ref={ref}
      data-slot="source-card"
      data-highlighted={highlighted || undefined}
      className={cn(
        "group/item relative flex items-start gap-2 px-3 transition-colors",
        "min-h-8 py-1.5",
        highlighted ? "bg-primary-subtle" : (interactive || collapsible) && LIST_ROW_HOVER,
        className,
      )}
      {...props}
    >
      {!group && (
        <span className="flex h-4 shrink-0 items-center text-ink-muted">
          <Icon icon={SOURCE_TYPE_ICON[type]} size={12} />
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {collapsible ? (
          <div className="flex items-start gap-1">
            {titleWithTooltip}
            {/* Its ::after covers the row: a click anywhere expands; the title, above it, opens. */}
            <button
              type="button"
              aria-expanded={open}
              aria-label={labels.excerpt}
              onClick={() => setChosenOpen(!open)}
              className={cn(
                "flex h-4 shrink-0 cursor-pointer items-center text-ink-muted outline-hidden group-hover/item:text-ink",
                "after:absolute after:inset-0",
                FOCUS_RING_INSET_AFTER,
              )}
            >
              <Icon
                icon={ChevronDown}
                size={12}
                className={cn("transition-transform duration-150 motion-reduce:transition-none", open && "rotate-180")}
              />
            </button>
          </div>
        ) : (
          titleWithTooltip
        )}
        {compact ? (
          // One line under the title: meta, then the snippet, cut together, so the title keeps the width.
          (metaLine || (hasSnippet && !snippetIsTitle)) && (
            <div className="truncate font-body text-caption text-ink-muted">
              {metaLine && <span className="font-sans text-caption tabular-nums">{metaLine}</span>}
              {metaLine && hasSnippet && !snippetIsTitle && <span aria-hidden="true"> · </span>}
              {hasSnippet && !snippetIsTitle && snippet}
            </div>
          )
        ) : (
          <>
            {metaLine && <span className="font-sans text-caption text-ink-muted tabular-nums">{metaLine}</span>}
            {hasSnippet && open && !snippetIsTitle && (
              <div className="font-body text-caption text-ink-muted whitespace-pre-line wrap-break-word">{snippet}</div>
            )}
          </>
        )}
        {open && children}
      </div>
      {!group && (
        <span className="relative z-10 flex h-4 shrink-0 items-center">
          <MachineScopeSlot labels={{ tooltip: machineScope }} size={12} />
        </span>
      )}
      {actions != null && (
        <div
          data-slot="actions"
          className={cn(
            "relative z-10 -my-1 flex size-7 shrink-0 items-center justify-center transition-opacity",
            LIST_ROW_ACTIONS_REVEAL,
          )}
        >
          {actions}
        </div>
      )}
    </li>
  );
});
